import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { rem } from 'polished'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import type CharterBundleModalUspItem from './CharterBundleModalUspItem'
import LineMaldivianIcon from 'LuxKit/Icons/line/LineMaldivianIcon'
import LineClockWithTickIcon from 'LuxKit/Icons/line/LineClockWithTickIcon'
import LineCalendarCheckIcon from 'LuxKit/Icons/line/LineCalendarCheckIcon'
import LineStarIcon from 'LuxKit/Icons/line/LineStarIcon'
import LuxuryEscapesSmallLogo from 'components/App/Header/Logo/logos/LuxuryEscapesSmallLogo'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'

export function CharterPartnerIcon() {
  return (
    <Group direction="horizontal" gap={8} verticalAlign="center" style={{ height: rem(40) }}>
      <LuxuryEscapesSmallLogo height={25} width={25} />
      <BodyText variant="large">x</BodyText>
      <LineMaldivianIcon style={{ width: rem(110), height: rem(20) }} />
    </Group>
  )
}

type CharterLearnMoreUsp = React.ComponentProps<typeof CharterBundleModalUspItem>
type CharterLearnMoreUspWithId = CharterLearnMoreUsp & {
  uspId: MaldivesUspId
}
type CharterLearnMoreMobileUspWithId = Partial<CharterLearnMoreUsp> & {
  uspId: MaldivesUspId
}

export const MALDIVES_USP_IDS = {
  saveTransitTime: 'saveTransitTime',
  privateCharter: 'privateCharter',
  maximiseHolidayTime: 'maximiseHolidayTime',
  flexibleBookings: 'flexibleBookings',
} as const

export type MaldivesUspId = (typeof MALDIVES_USP_IDS)[keyof typeof MALDIVES_USP_IDS]

export function useMaldivesUspGroup1(): Array<CharterLearnMoreUspWithId> {
  const intl = useIntl()
  return [
    {
      uspId: MALDIVES_USP_IDS.saveTransitTime,
      title: intl.formatMessage({
        id: 'h9K9BN4mtV',
        defaultMessage: 'Save up to 20 hours in transit*',
        description: 'USP title about transit time savings for Maldives charter flight',
      }),
      description: intl.formatMessage({
        id: 'hHEE+4LVIE',
        defaultMessage: 'Fly direct from Melbourne to Malé in less than 12 hours, with no more international stopovers.',
        description: 'USP description about direct Melbourne to Malé charter flight',
      }),
      icon: <LinePlaneDepartureIcon />,
      tabletIcon: <LinePlaneDepartureIcon size="XL" style={{ display: 'block' }} />,
    },
    {
      uspId: MALDIVES_USP_IDS.privateCharter,
      title: intl.formatMessage({
        id: 'wLEEeY2QA6',
        defaultMessage: 'Luxury Escapes private charter',
        description: 'USP title about Luxury Escapes private Maldives charter',
      }),
      description: intl.formatMessage({
        id: 'nrt/OsNluH',
        defaultMessage: 'Weekly direct flights aboard a full-service Airbus A330-200, reserved exclusively for Luxury Escapes customers.',
        description: 'USP description about private charter aircraft details',
      }),
      icon: <CharterPartnerIcon />,
      tabletIcon: <CharterPartnerIcon />,
    },
  ]
}

export function useMaldivesUspGroup2(): Array<CharterLearnMoreUspWithId> {
  const intl = useIntl()
  return [
    {
      uspId: MALDIVES_USP_IDS.maximiseHolidayTime,
      title: intl.formatMessage({
        id: 'ao1AJjmMme',
        defaultMessage: 'Maximise your holiday time',
        description: 'USP title about maximizing holiday time with Maldives charter',
      }),
      description: intl.formatMessage({
        id: 'URtd1XatMe',
        defaultMessage: 'Our overnight flights paired with early check-in & late checkout give you extra time to enjoy the Maldives.',
        description: 'USP description about overnight flights and extended check-in/checkout',
      }),
      icon: <LineClockWithTickIcon />,
      tabletIcon: <LineClockWithTickIcon size="XL" style={{ display: 'block' }} />,
    },
    {
      uspId: MALDIVES_USP_IDS.flexibleBookings,
      title: intl.formatMessage({
        id: 'g7wGqbk1ma',
        defaultMessage: 'Flexible bookings',
        description: 'USP title about flexible booking options for Maldives charter',
      }),
      description: intl.formatMessage({
        id: 'puapei8Cas',
        defaultMessage: 'Experience peace of mind with free cancellation within 7 days of purchase and flexible date-change options. Conditions apply.',
        description: 'USP description about free cancellation and date-change flexibility',
      }),
      icon: <LineCalendarCheckIcon />,
      tabletIcon: <LineCalendarCheckIcon size="XL" style={{ display: 'block' }} />,
    },
  ]
}

export function useMaldivesUspMobile(): Array<CharterLearnMoreMobileUspWithId> {
  const intl = useIntl()
  return [
    {
      uspId: MALDIVES_USP_IDS.saveTransitTime,
      title: intl.formatMessage({
        id: 'oJ4NLHNGcM',
        defaultMessage: 'Save up to 20 hours in transit*',
        description: 'Mobile USP title about transit time savings for Maldives charter flight',
      }),
      description: <FormattedMessage
        id="ySOd5Gnik+"
        defaultMessage="<strong>Save up to 20 hours in transit*</strong> with nonstop flights from Melbourne to Malé in less than 12 hours."
        description="Mobile USP description about direct Melbourne to Malé charter flight"
        values={{
          strong: (chunks) => <strong>{chunks}</strong>,
        }}
      />,
      icon: <LinePlaneDepartureIcon />,
    },
    {
      uspId: MALDIVES_USP_IDS.privateCharter,
      title: intl.formatMessage({
        id: '8IXaNmWYgX',
        defaultMessage: 'Luxury Escapes private charter',
        description: 'Mobile USP title about Luxury Escapes private Maldives charter',
      }),
      description: <FormattedMessage
        id="x5bIURGP31"
        defaultMessage="<strong>Luxury Escapes private charter</strong> aboard a Maldivian Airlines full-service Airbus A330-200."
        description="Mobile USP description about private charter aircraft"
        values={{
          strong: (chunks) => <strong>{chunks}</strong>,
        }}
      />,
      icon: <LineStarIcon />,
    },
    {
      uspId: MALDIVES_USP_IDS.maximiseHolidayTime,
      title: intl.formatMessage({
        id: 'Ul5ztZUOlF',
        defaultMessage: 'Maximise your holiday time',
        description: 'Mobile USP title about maximizing holiday time with Maldives charter',
      }),
      description: <FormattedMessage
        id="Bn1Cy6iaB4"
        defaultMessage="<strong>Maximise your holiday time</strong> with early check-in & late checkout."
        description="Mobile USP description about overnight flights and extended check-in/checkout"
        values={{
          strong: (chunks) => <strong>{chunks}</strong>,
        }}
      />,
      icon: <LineClockWithTickIcon />,
    },
    {
      uspId: MALDIVES_USP_IDS.flexibleBookings,
      title: intl.formatMessage({
        id: 'otR1iIwEmt',
        defaultMessage: 'Flexible bookings',
        description: 'Mobile USP title about flexible booking options for Maldives charter',
      }),
      description: <FormattedMessage
        id="uCMLgmUip9"
        defaultMessage="<strong>Free cancellation</strong> within 7 days and flexible date-change options. Conditions apply."
        description="Mobile USP description about free cancellation and date-change flexibility"
        values={{
          strong: (chunks) => <strong>{chunks}</strong>,
        }}
      />,
      icon: <LineCalendarCheckIcon />,
    },
  ]
}
